<template>
  <page :title="isEditMode ? 'updatePerformanceTitle': 'newPerformanceTitle'" :textHeight="(WindowSize.y - 276) + 'px'">
    <template #pageContainer>
      <v-alert v-if="isCreated" type="success">{{ $t(`text.newPerformanceInfo`) }}</v-alert>
      <form>
		<v-row><v-col>
			<v-autocomplete outlined dense
				hide-details="auto"
				:disabled="loading || !anyCompetition || isEditMode"
				:readonly="hasOnlyOneCompetition"
				v-model="competition.selected"
				:items="competition.items"
				:error-messages="competitionErrors"
				required
				:label="$t('text.competition')"
				:loading="competitionLoading"
				@input="$v.competition.selected.$touch()"
				@blur="$v.competition.selected.$touch()">
			</v-autocomplete>
		</v-col></v-row>
		<v-row><v-col>
			<v-menu v-model="menu" :close-on-content-click="false" max-width="290">
				<template #activator="{ on }">
					<v-text-field outlined dense
						hide-details="auto"
						class="mt-0 pt-0"
						:value="FormattedDate"
						:error-messages="dateErrors"
						:disabled="isEditMode"
						clearable
						:label="$t('text.date')"
						readonly
						v-on="on"
						@click:clear="date = null"
						required
						@input="$v.date.$touch()"
						@blur="$v.date.$touch()">
					</v-text-field>
				</template>
				<v-date-picker
					locale="DE-de" v-model="date" @change="menu = false">
				</v-date-picker>
			</v-menu>
		</v-col></v-row>
		<v-row><v-col>
			<v-autocomplete outlined dense
				hide-details="auto"
				:disabled="loading || !anyClassDef || isEditMode"
				:readonly="hasOnlyOneClassDef"
				v-model="classDef.selected"
				:items="classDef.items"
				:error-messages="classDefErrors"
				required
				:label="$t('text.class')"
				:loading="classDefLoading"
				@input="$v.classDef.selected.$touch()"
				@blur="resetForm()"
				@change="resetForm()">
			</v-autocomplete>
		</v-col></v-row>
		<v-row><v-col>
			<v-autocomplete outlined dense
				hide-details="auto"
				:disabled="loading || !anyEvent || isEditMode"
				:readonly="hasOnlyOneEvent"
				v-model="event.selected"
				:items="event.items"
				:error-messages="eventErrors"
				required
				:label="$t('text.event')"
				:loading="eventLoading"
				@input="$v.event.selected.$touch()"
				@blur="resetForm()"
				@change="resetForm()">
			</v-autocomplete>
		</v-col></v-row>
		<v-row v-show="isRelayEvent || isMultiTeamEvent || isTeamEndurance"><v-col>
			<v-autocomplete outlined dense
				hide-details="auto"
				:disabled="loading || isEditMode"
				v-model="organization.selected"
				:items="organization.items"
				:error-messages="orgErrors"
				required
				hide-no-data
				:label="$t('text.club')"
				:loading="orgLoading"
				@keyup="filterOrganizations"
				@input="$v.organization.selected.$touch()"
				@blur="$v.organization.selected.$touch()">
			</v-autocomplete>
		</v-col></v-row>
		<v-row v-show="!isRelayEvent && !isMultiTeamEvent && !isTeamEndurance"><v-col>
			<v-autocomplete outlined dense
				hide-details="auto"
				:disabled="loading"
				v-model="competitor.selected"
				:items="competitor.items"
				:error-messages="competitorErrors"
				required
				hide-no-data
				:label="$t('text.competitor')"
				:loading="competitorLoading"
				@keyup="filterCompetitors"
				@input="$v.competitor.selected.$touch()"
				@blur="$v.competitor.selected.$touch()">
			</v-autocomplete>
		</v-col></v-row>
		<v-row><v-col>
			<v-text-field outlined dense
				hide-details="auto"
				:label="$t('text.bestResult')"
				v-model="bestResult"
				:error-messages="bestResultErrors"
				:disabled="isMultiEvent"
				required
				@keyup="formatbestResult"
				@input="$v.bestResult.$touch()"
				@blur="$v.bestResult.$touch()">
			</v-text-field>
		</v-col></v-row>
		<template v-if="!isRelayEvent && !isMultiTeamEvent">
			<v-row v-show="isHeightWeight"><v-col>
				<v-text-field outlined dense
					hide-details="auto"
					:label="$t('text.heightWeight')"
					v-model="heightWeight"
					:error-messages="heightWeightErrors"
					required
					@input="$v.heightWeight.$touch()"
					@blur="$v.heightWeight.$touch()">
				</v-text-field>
			</v-col></v-row>
			<v-row v-show="isWind"><v-col>
				<v-text-field outlined dense
					hide-details="auto"
					:label="$t('text.windResult')"
					v-model="windResult"
					:error-messages="windResultErrors"
					required
					@keyup="formatWind"
					@input="$v.windResult.$touch()"
					@blur="$v.windResult.$touch()">
				</v-text-field>
			</v-col></v-row>
		</template>
		<multi-event-editor
			v-if="isMultiEvent"
			:subEvents="subEvents"
			@validate="onMultiValidate"
			@input="onMEValuesChanged"
			v-model="multiEventResults"
			:eventClass="eventClass"
			:environment="getEnvironment">
		</multi-event-editor>
        <relay-editor
			v-if="!loading && (isRelayEvent || isMultiTeamEvent || isTeamEndurance)"
			:loading="loading"
			:competitors="competitor.items"
			:competitorLoading="competitorLoading"
			:relayMembers="getSelectedEvent.relayMembers"
			:relayCompList="relayCompetitor.list"
			:multiTeamEvent="isMultiTeamEvent || isTeamEndurance"
			:measurement="getSelectedEvent.measurement"
			@competitor-changed="onCompetitorChanged"
			@competitor-search="filterCompetitors">
		</relay-editor>
		<div v-if="isEditMode">
			<div>{{ $t(`text.addedFrom`) }} {{creatorUser}} {{ $t(`text.on`) }} {{createdDateTime}}</div>
			<div v-if="hasBeenEdited">{{ $t(`text.editedFrom`) }} {{lastEditorUser}} {{ $t(`text.on`) }} {{lastEditDatetime}}</div>
			<br>
		</div>
      </form>
      <spinner :loading="loading"></spinner>
    </template>
	<template #pageActions>
		<v-row><v-col>
			<v-btn
				class="mr-4"
				:disabled="$v.$invalid || loading || !isMultiEventValid"
				@click="create">
				{{ $t(`text.createAndNew`) }}
			</v-btn>
			<v-btn
				class="mr-4"
				:disabled="$v.$invalid || loading || !isMultiEventValid"
				@click="createAndClose">
				{{ $t(`text.createAndClose`) }}
			</v-btn>
			<v-btn
				:disabled="loading"
				class="mr-4"
				@click="back">
				{{ $t(`text.abortNoSave`) }}
			</v-btn>
		</v-col></v-row>
	</template>
  </page>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { required, decimal, requiredIf } from 'vuelidate/lib/validators';

import Page from '../general/Page.vue';
import Spinner from '../general/Spinner.vue';
import RelayEditor from './RelayEditor.vue';
import MultiEventEditor from './MultiEventEditor.vue';

import performanceActions from '../store/actions/performance';
import userActions from '../store/actions/user';
import competitionActions from '../store/actions/competition';
import bestListActions from '../store/actions/bestList';
import namespace from '../store/namespace';

import modelState from '../mixins/modelState';
import formater from '../mixins/formater';

export default {
	name: 'NewPerformance',
	mixins: [modelState, formater],
	components: {
		Page,
		Spinner,
		RelayEditor,
		MultiEventEditor,
	},
	validations: {
		event: {
			selected: {
				required,
			},
		},
		competition: {
			selected: {
				required,
			},
		},
		classDef: {
			selected: {
				required,
			},
		},
		organization: {
			selected: {
				// eslint-disable-next-line func-names
				required: requiredIf(function () {
					return this.isRelayEvent || this.isMultiTeamEvent || this.isTeamEndurance;
				}),
			},
		},
		competitor: {
			selected: {
				// eslint-disable-next-line func-names
				required: requiredIf(function () {
					return !this.isRelayEvent && !this.isMultiTeamEvent && !this.isTeamEndurance;
				}),
			},
		},
		bestResult: {
			required,
		},
		windResult: {
			decimal,
		},
		heightWeight: {
			decimal,
		},
		date: {},
	},
	data() {
		return {
			event: {
				items: [],
				selected: this.$route.params.eventCode,
			},
			classDef: {
				items: [],
				selected: this.$route.params.classCode,
			},
			competition: {
				items: [],
				selected: '',
			},
			competitor: {
				items: [],
				selected: this.nullCompetitor,
			},
			relayCompetitor: {
				list: [],
			},
			organization: {
				items: [],
				selected: null,
			},
			bestResult: null,
			windResult: null,
			heightWeight: null,
			eventClass: null,
			id: this.$route.params.id || null,
			date: null,
			subEvents: [],
			multiEventValid: false,
			multiEventResults: [],
			orgLoading: false,
			competitorLoading: false,
			eventLoading: false,
			classDefLoading: false,
			competitionLoading: false,
			loading: false,
			menu: false,
			creatorUserId: null,
			creatorUser: null,
			createdDateTime: null,
			lastEditorUserId: null,
			lastEditorUser: null,
			lastEditDatetime: null,
			success: false,
			get Success() {
				return this.success;
			},
			// eslint-disable-next-line vue/no-dupe-keys
			set Success(value) {
				this.success = value;
				if (value) {
					setTimeout(() => {
						this.Success = false;
					}, 5000);
				}
			},
		};
	},
	computed: {
		...mapGetters(['WindowSize']),
		...mapGetters(namespace.PERFORMANCE, ['Links', 'Entities']),
		...mapGetters(namespace.USER, ['User']),
		...mapGetters(namespace.ORGANIZATION, ['Organization']),
		competitionErrors() {
			const errors = [];

			if (!this.$v.competition.selected.$dirty) return errors;

			const apiErrors = this.validateProperty('Competition');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.competition.selected.required
        && errors.push(this.$i18n.t('text.errorCompetitionRequired'));

			return errors;
		},
		dateErrors() {
			const errors = [];

			if (!this.$v.date.$dirty) return errors;

			const apiErrors = this.validateProperty('ResultDate');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.competition.selected.required
        && errors.push(this.$i18n.t('text.errorResultDate'));

			return errors;
		},
		eventErrors() {
			const errors = [];

			if (!this.$v.event.selected.$dirty) return errors;

			const apiErrors = this.validateProperty('Event');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.event.selected.required
        && errors.push(this.$i18n.t('text.errorEventRequired'));

			return errors;
		},
		classDefErrors() {
			const errors = [];

			if (!this.$v.classDef.selected.$dirty) return errors;

			const apiErrors = this.validateProperty('ClassDef');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.classDef.selected.required
        && errors.push(this.$i18n.t('text.errorClassDefRequired'));

			return errors;
		},
		orgErrors() {
			const errors = [];

			if (!this.$v.organization.selected.$dirty) return errors;

			const apiErrors = this.validateProperty('Organization');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.organization.selected.required
        && errors.push(this.$i18n.t('text.errorOrganizationRequired'));

			return errors;
		},
		competitorErrors() {
			const errors = [];

			if (!this.$v.competitor.selected.$dirty) return errors;

			const apiErrors = this.validateProperty('Competitor');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.competitor.selected.required
        && errors.push(this.$i18n.t('text.errorCompetitorRequired'));

			return errors;
		},
		nullCompetitor() {
			return this.isRelayEvent || this.isMultiTeamEvent || this.isTeamEndurance ? '00000000-0000-0000-0000-000000000000' : null;
		},
		bestResultErrors() {
			const errors = [];

			if (!this.$v.bestResult.$dirty) return errors;

			const apiErrors = this.validateProperty('BestResult');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.bestResult.required
        && errors.push(this.$i18n.t('text.errorBestResultRequired'));

			return errors;
		},
		windResultErrors() {
			const errors = [];

			if (!this.$v.windResult.$dirty) return errors;

			const apiErrors = this.validateProperty('WindResult');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.windResult.decimal
        && errors.push(this.$i18n.t('text.errorShouldBeDecimal'));

			return errors;
		},
		heightWeightErrors() {
			const errors = [];

			if (!this.$v.heightWeight.$dirty) return errors;

			const apiErrors = this.validateProperty('HeightWeight');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.heightWeight.decimal
        && errors.push(this.$i18n.t('text.errorShouldBeDecimal'));

			return errors;
		},
		FormattedDate() {
			return this.date ? this.$moment(this.date).format('DD.MM.YYYY') : '';
		},
		isWind() {
			return (
				this.eventClass
				&& this.eventClass.wind
				&& this.getSelectedCompetition?.environment != 0
			);
		},
		getSelectedEvent() {
			return this.event.items.find(x => x.value == this.event.selected) || null;
		},
		getSelectedClass() {
			return this.classDef.items.find(x => x.value == this.classDef.selected) || null;
		},
		getSelectedCompetition() {
			return this.competition.items.find(
				x => x.value == this.competition.selected,
			) || null;
		},
		isRelayEvent() {
			return !!(this.getSelectedEvent && this.getSelectedEvent.isRelayEvent);
		},
		isMultiTeamEvent() {
			return !!(this.getSelectedEvent && this.getSelectedEvent.isMultiTeamEvent);
		},
		isMultiEvent() {
			return !!(this.getSelectedEvent && this.getSelectedEvent.isMultiEvent);
		},
		isMultiEventValid() {
			return !this.isMultiEvent || this.multiEventValid;
		},
		isHeightWeight() {
			return this.eventClass && this.eventClass.heightWeight;
		},
		isTeamEndurance() {
			return !!(this.getSelectedEvent && this.getSelectedEvent.eventType === 14);
		},
		hasOnlyOneEvent() {
			return this.event.items.length === 1;
		},
		anyEvent() {
			return this.event.items.length > 0;
		},
		hasOnlyOneClassDef() {
			return this.classDef.items.length === 1;
		},
		anyClassDef() {
			return this.classDef.items.length > 0;
		},
		hasOnlyOneCompetition() {
			return this.competition.items.length === 1;
		},
		anyCompetition() {
			return this.competition.items.length > 0;
		},
		anyCompetitor() {
			return this.competitor.items.length > 0;
		},
		isCreated() {
			return this.Success;
		},
		isEditMode() {
			return !!this.id;
		},
		hasBeenEdited() {
			return this.lastEditorUserId != null;
		},
		getEnvironment() {
			return this.getSelectedCompetition?.environment;
		},
	},
	watch: {
		'event.selected': function () {
			if (this.classDef.selected) {
				this.eventAndClassSelected();
			}
		},
		'classDef.selected': function () {
			if (this.event.selected) {
				this.eventAndClassSelected();
			}
		},
		'competition.selected': function () {
			const comp = this.competition.items.find(
				x => x.value === this.competition.selected,
			);
			if (comp) this.date = comp.dateFrom.substr(0, 10);
		},
	},
	created() {
		this.resetForm();
	},
	async mounted() {
		if (this.Links.length === undefined || this.Links.length === 0) {
			await this.FETCH_CREATE_LINKS();
		}

		await this.getEvents();
		await this.getCompetitions();
		await this.getClassDefs();

		if (this.isEditMode && this.id) {
			await this.getPerformance();
		}
	},
	methods: {
		...mapActions(namespace.PERFORMANCE, [
			performanceActions.GET_PERFORMANCE_BY_ID,
			performanceActions.CREATE_NEW_PERFORMANCE,
			performanceActions.UPDATE_PERF,
			performanceActions.FETCH_EVENTS,
			performanceActions.FETCH_CREATE_LINKS,
			performanceActions.FETCH_CLASSES,
			performanceActions.GET_CREATED_PERFORMANCES,
			performanceActions.FETCH_EVENT_CLASS,
			performanceActions.FETCH_GLOBAL_COMPETITORS,
			performanceActions.GET_SUBEVENTS_FOR_MULTI,
		]),
		...mapActions(namespace.COMPETITION, [
			competitionActions.FETCH_COMPETITIONS_FOR_SELECTOR,
		]),
		...mapActions(namespace.BESTLIST, [
			bestListActions.GET_BRANCHES_FOR_PERFDB,
			bestListActions.GET_FILTERED_BRANCHES_FOR_PERFDB,
		]),
		...mapActions(namespace.USER, [
			userActions.GET_USER,
		]),
		async eventAndClassSelected() {
			await this.getEventClass();
			await this.getSubEvents();
		},
		async filterCompetitors(val) {
			if (val.keyCode >= 45 && val.target.value && val.target.value.trim().length >= 3) {
				await this.getGlobalCompetitors(val.target.value);
			}
		},
		async filterOrganizations(val) {
			if (val.keyCode >= 45 && val.target.value && val.target.value.trim().length >= 3) {
				await this.getOrganizations(val.target.value);
			}
		},
		async resetForm() {
			this.competitor.selected = this.nullCompetitor;

			this.organization.selected = (this.isRelayEvent || this.isMultiTeamEvent || this.isTeamEndurance) ? null : '00000000-0000-0000-0000-000000000000';
			this.relayCompetitor.list = [];
			this.bestResult = '';
			this.windResult = '';
			this.heightWeight = '';
			//this.subEvents = [];
			this.getEventClass();
		},
		onCompetitorChanged(relayCompetitorList) {
			this.relayCompetitor.list = relayCompetitorList;
		},
		onMEValuesChanged() {
			this.bestResult = 0;
			for (let i = 0; i < this.multiEventResults.length; i += 1) {
				this.bestResult += this.multiEventResults[i].points || 0;
			}
		},
		onMultiValidate(v) {
			this.multiEventValid = v;
			if (v) {
				this.onMEValuesChanged();
			}
		},
		async getPerformance() {
			this.loading = true;
			try {
				const performance = await this.GET_PERFORMANCE_BY_ID(this.id);

				if (performance) {
					this.id = performance.id;
					this.event.selected = performance.eventShortNational;
					this.classDef.selected = performance.classShort;
					this.competition.selected = performance.competitionId;
					this.date = performance.performanceDateTime;
					this.windResult = performance.bestWind;
					this.multiEventResults = performance.multiEventResults;
					this.formatWind();
					this.bestResult = performance.bestResultString;
					this.creatorUserId = performance.creatorUserId;
					const creator = await this.GET_USER(performance.creatorUserId);
					this.creatorUser = creator.userName;
					this.createdDateTime = this.$moment(performance.createdDatetime).format('DD.MM.YYYY');
					if (performance.isRelayEvent || performance.isMultiTeamEvent || this.getSelectedEvent.eventType === 14) {
						this.filterOrganizations({ keyCode: 100, target: { value: performance.organizationId } });
						this.relayCompetitor.list = performance.relayMembers.map(x => ({
							guid: x.competitorId,
							name: x.fullName,
							value: x.value,
						}));
					} else {
						this.filterCompetitors({ keyCode: 100, target: { value: performance.competitorId } });
					}
					if (performance.lastEditorUserId != null) {
						this.lastEditorUserId = performance.lastEditorUserId;
						const editor = await this.GET_USER(performance.lastEditorUserId);
						this.lastEditorUser = editor.userName;
						this.lastEditDatetime = this.$moment(performance.lastEditDatetime).format('DD.MM.YYYY');
					}
				}
			} finally {
				this.loading = false;
			}
		},
		async createAndClose() {
			return this.createPerf(true);
		},
		async create() {
			return this.createPerf(false);
		},
		async createPerf(close) {
			this.loading = true;
			this.$v.$touch();

			if (!this.$v.$invalid) {
				let link = '';
				if (!this.isEditMode) {
					link = this.Links.find(x => x.rel === 'new');
				} else {
					link = this.Links.find(x => x.rel === 'update');
				}

				if (link) {
					this.Success = false;
					const baseData = {
						bestResult: this.bestResult,
						windResult: this.windResult,
						heightWeight: this.heightWeight,
						organizationId: this.organization.selected,
						competitorId: this.competitor.selected,
						performanceDateTime: this.date,
						relayMembers: this.relayCompetitor.list.map(x => ({ competitorId: x.guid, value: x.value })),
					};

					if (this.isRelayEvent || this.isMultiTeamEvent || this.isTeamEndurance) baseData.competitorId = this.nullCompetitor;
					baseData.multiEventResults = this.isMultiEvent ? this.multiEventResults : null;

					try {
						this.modelState = {};
						let newPerf = {};
						if (!this.isEditMode) {
							const newP = {
								competitionId: this.competition.selected,
								eventId: this.event.selected,
								classId: this.classDef.selected,
								creatorUserId: this.User.id,
								creatorOrganisationId: this.Organization,
							};
							newPerf = { ...baseData, ...newP };
							// console.log(newPerf);
							await this.CREATE_NEW_PERFORMANCE({ newPerf, link });
						} else {
							const updatePerf = {
								id: this.id,
								lastEditorUserId: this.User.id,
							};
							newPerf = { ...baseData, ...updatePerf };
							// console.log(newPerf);
							await this.UPDATE_PERF({ newPerf, link });
						}

						this.Success = true;
						this.competitor.selected = this.nullCompetitor;
						this.relayCompetitor.list = [];
						this.bestResult = null;
						this.multiEventResults = [];
						this.$v.$reset();
					} catch (e) {
						this.Success = false;
						if (e.response.status === 422) {
							this.modelState = e.response.data;
							if (this.modelError()) {
								throw new Error(this.modelError());
							}
						}

						throw e;
					} finally {
						this.loading = false;
					}
				}
			}
			this.loading = false;
			if (close) {
				this.back();
			}
		},
		back() {
			this.$router.go(-1);
		},
		async getEvents() {
			this.eventLoading = true;
			try {
				this.event.items = await this.FETCH_EVENTS(this.Organization);

				if (this.event.items.length === 1) {
					this.event.selected = this.event.items[0].value;
				}
			} finally {
				if (!this.isEditMode) this.resetForm();
				this.eventLoading = false;
			}
		},
		async getSubEvents() {
			if (this.isMultiEvent) {
				this.loading = true;
				try {
					const result = await this.GET_SUBEVENTS_FOR_MULTI({ eId: this.getSelectedEvent.id, cId: this.getSelectedClass.id });
					const task = [];
					for (let i = 0; i < result.length; i += 1) {
						task.push(this.FETCH_EVENT_CLASS({
							eventCode: result[i].value,
							classCode: this.classDef.selected,
						}));
					}
					const ec = await Promise.all(task);
					ec.forEach((v, i) => { result[i].ec = v; });
					this.subEvents = result;
				} finally {
					this.loading = false;
				}
			}
		},
		async getCompetitions() {
			this.competitionLoading = true;
			try {
				this.competition.items = await this.FETCH_COMPETITIONS_FOR_SELECTOR(this.Organization);

				if (this.competition.items.length === 1) {
					this.competition.selected = this.competition.items[0].value;
				}
			} finally {
				this.competitionLoading = false;
			}
		},
		async getClassDefs() {
			this.classDefLoading = true;
			try {
				this.classDef.items = await this.FETCH_CLASSES(this.Organization);

				if (this.classDef.items.length === 1) {
					this.classDef.selected = this.classDef.items[0].value;
				}
			} finally {
				this.classDefLoading = false;
			}
		},
		async getGlobalCompetitors(query) {
			this.competitorLoading = true;
			try {
				this.competitor.items = await this.FETCH_GLOBAL_COMPETITORS({performanceListOrOrgGuid: this.Organization, query:query});

				if (this.competitor.items.length === 1) {
					this.competitor.selected = this.competitor.items[0].value;
				}
			} finally {
				this.competitorLoading = false;
			}
		},
		async getOrganizations(query) {
			this.orgLoading = true;
			try {
				this.organization.items = await this.GET_FILTERED_BRANCHES_FOR_PERFDB({ id: this.User.masterOrganization.externalId, query });

				if (this.organization.items.length === 1) {
					this.organization.selected = this.organization.items[0].value;
				}
			} finally {
				this.orgLoading = false;
			}
		},
		async getEventClass() {
			try {
				if (this.event?.selected && this.classDef?.selected) {
					this.eventClass = await this.FETCH_EVENT_CLASS({
						eventCode: this.event.selected,
						classCode: this.classDef.selected,
					});

					if (this.isHeightWeight) {
						this.heightWeight = this.eventClass.heightWeight;
					}
				}
			} catch (e) {
				console.log(e);
				// console.log(eventClassRequest);
			}
		},
		formatbestResult() {
			this.bestResult = this.gFormatbestResult(this.bestResult, this.getSelectedEvent?.measurement);
		},
		formatWind() {
			this.windResult = this.gFormatWind(this.windResult);
		},
	},
};
</script>

<style lang="scss" scoped>
</style>
